// =============================================================================
// src/features/explore/explore-g26-constants.ts — Literais do modo "Eleicoes
//   Gerais 2026" do Explorar (assets, rotulos, cores dos partidos de 2026).
//
// Pedido 2026-10-13: "todos absolutamente todos os dados da ultima eleicao".
//   Assets gerados por scripts/build-tse-2026.mjs (TSE, 1o turno 04/10/2026).
//   O 2o turno (25/10/2026) entra como `-t2-` re-rodando o gerador com --turno=2.
// =============================================================================

import type { HexColor } from '../../app/types';
import type { G26CargoCode } from './explore-g26-types';
import { PARTY_COLORS } from './constants';

/** Valor do item "2026" no menu de Ano (nao e um ElectoralYear historico). */
export const G26_YEAR_VALUE = 'g26';

/** URLs (relativas, como os geo-*.json) dos assets do 1o turno. */
export const G26_ASSET_URLS = {
  dataset: 'assets/tse-2026-t1-am.json',
  locais: 'assets/tse-2026-t1-am-locais.json',
  locaisVotos: (cargo: G26CargoCode): string => `assets/tse-2026-t1-am-locais-${cargo}.json`,
} as const;

/** Cargo aberto por padrao ao entrar em 2026 (a disputa do cliente). */
export const G26_DEFAULT_CARGO: G26CargoCode = 'governador';

/** Rotulo curto do cargo nos chips/legenda. */
export const G26_CARGO_SHORT: Record<G26CargoCode, string> = {
  presidente: 'Presidente',
  governador: 'Governador',
  senador: 'Senador',
  depfederal: 'Dep. Federal',
  depestadual: 'Dep. Estadual',
};

/** Rotulo do chip de Ano no modo 2026 (cargo vai no chip proprio). */
export const G26_YEAR_LABEL = '2026 · 1º turno';

/** Nota de proveniencia exibida no card/popup. */
export const G26_SOURCE_NOTE = 'TSE · Eleições Gerais 2026 · 1º turno (04/10/2026) · dados oficiais';

/**
 * Cores dos partidos que disputaram 2026 e NAO estao na paleta historica
 * (PARTY_COLORS). Escolhidas p/ distinguir no mapa; a paleta historica vale primeiro.
 */
const G26_EXTRA_PARTY_COLORS: Record<string, HexColor> = {
  PSOL: '#B5179E', NOVO: '#F28C28', REDE: '#2A9D8F', PDT: '#C0392B', PCDOB: '#8B1E1E',
  PODE: '#2E86AB', SOLIDARIEDADE: '#F4A261', AGIR: '#6C757D', MISSÃO: '#3D5A80',
  MOBILIZA: '#9C6644', DC: '#457B9D', PSTU: '#A4161A', PCB: '#7B2CBF', PCO: '#9D0208',
  UP: '#D00000', DEMOCRATA: '#264653',
};

/** Paleta de reserva p/ siglas fora das duas tabelas (deterministica por hash). */
const G26_FALLBACK_PALETTE: readonly HexColor[] = ['#5C677D', '#8D6E63', '#00897B', '#7CB342', '#C2185B', '#5E35B1'];

/**
 * Cor do partido em 2026: paleta historica -> tabela de 2026 -> reserva por hash.
 *
 * @example g26PartyColor('PSD') // '#378ADD' (mesma cor do historico)
 */
export function g26PartyColor(sigla: string): HexColor {
  const known = PARTY_COLORS[sigla] ?? G26_EXTRA_PARTY_COLORS[sigla];
  if (known) return known;
  let hash = 0;
  for (const ch of sigla) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return G26_FALLBACK_PALETTE[hash % G26_FALLBACK_PALETTE.length] as HexColor;
}
